import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Trash2, Edit2, FileDown, ArrowLeft, Syringe, Stethoscope, Pill, Scissors, BookOpen, DollarSign, Heart, Trophy, Camera, Loader2, Sparkles, Image as ImageIcon, Upload, Clock, Share2, Copy, Check, X, CheckCircle2, ArrowUpDown, Star } from 'lucide-react';

const RECORD_CONFIGS = {
  vaccinations: {
    collection: 'vaccination_records',
    label: 'Vaccination Records',
    icon: Syringe,
    bg: '#F9F7F3',
    fields: [
      { key: 'date', label: 'Date', type: 'date', required: true },
      { key: 'appointment_time', label: 'Time', type: 'time' },
      { key: 'vaccine', label: 'Vaccine', type: 'text', required: true },
      { key: 'administered_by', label: 'Administered By', type: 'text' },
      { key: 'next_due', label: 'Next Due', type: 'date' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    pdfType: 'vaccinations',
    hasComplete: true,
  },
  vet_visits: {
    collection: 'vet_visits',
    label: 'Veterinary Visits',
    icon: Stethoscope,
    bg: '#FFFFFF',
    fields: [
      { key: 'date', label: 'Date', type: 'date', required: true },
      { key: 'appointment_time', label: 'Time', type: 'time' },
      { key: 'visit_type', label: 'Visit Type', type: 'select', options: [
        { value: '', label: 'Select type...' },
        { value: 'wellness', label: 'Wellness / Annual Exam' },
        { value: 'sick_emergency', label: 'Sick / Emergency' },
        { value: 'lameness', label: 'Lameness Exam' },
        { value: 'dental', label: 'Dental Float' },
        { value: 'pre_purchase', label: 'Pre-Purchase Exam' },
        { value: 'coggins', label: 'Coggins / Health Certificate' },
        { value: 'other', label: 'Other' },
      ]},
      { key: 'reason', label: 'Reason / Details', type: 'text', required: true },
      { key: 'provider_name', label: 'Veterinarian', type: 'text' },
      { key: 'diagnosis', label: 'Diagnosis', type: 'text' },
      { key: 'treatment', label: 'Treatment', type: 'text' },
      { key: 'cost', label: 'Cost ($)', type: 'number' },
      { key: 'follow_up', label: 'Follow-Up Date', type: 'date' },
    ],
    pdfType: 'vet_visits',
    hasComplete: true,
  },
  specialty: {
    collection: 'specialty_services',
    label: 'Specialty Services',
    icon: Star,
    bg: '#F3E8FF',
    fields: [
      { key: 'date', label: 'Date', type: 'date', required: true },
      { key: 'appointment_time', label: 'Time', type: 'time' },
      { key: 'service_type', label: 'Service Type', type: 'select', required: true, options: [
        { value: '', label: 'Select type...' },
        { value: 'chiropractic', label: 'Chiropractic' },
        { value: 'massage', label: 'Equine Massage' },
        { value: 'acupuncture', label: 'Acupuncture' },
        { value: 'reproductive', label: 'Reproductive / Breeding Vet' },
        { value: 'dentist', label: 'Equine Dentist' },
        { value: 'nutritionist', label: 'Nutritionist' },
        { value: 'other', label: 'Other' },
      ]},
      { key: 'provider_name', label: 'Provider Name', type: 'text' },
      { key: 'cost', label: 'Cost ($)', type: 'number' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
      { key: 'follow_up', label: 'Follow-Up Date', type: 'date' },
    ],
    pdfType: 'specialty',
    hasComplete: true,
  },
  medications: {
    collection: 'medication_records',
    label: 'Medication Tracker',
    icon: Pill,
    bg: '#E9EFE8',
    fields: [
      { key: 'date', label: 'Date', type: 'date', required: true },
      { key: 'medication', label: 'Medication', type: 'text', required: true },
      { key: 'dosage', label: 'Dosage', type: 'text' },
      { key: 'time', label: 'Time', type: 'text' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    pdfType: 'medications',
  },
  farrier: {
    collection: 'farrier_records',
    label: 'Farrier Visit Record',
    icon: Scissors,
    bg: '#F9F7F3',
    fields: [
      { key: 'date', label: 'Date', type: 'date', required: true },
      { key: 'appointment_time', label: 'Time', type: 'time' },
      { key: 'trim_or_shoes', label: 'Trim / Shoes', type: 'text' },
      { key: 'hoof_condition', label: 'Hoof Condition', type: 'text' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
      { key: 'next_appointment', label: 'Next Appointment', type: 'date' },
    ],
    pdfType: 'farrier',
    hasComplete: true,
  },
  training: {
    collection: 'training_entries',
    label: 'Training Journal',
    icon: BookOpen,
    bg: '#FFFFFF',
    fields: [
      { key: 'date', label: 'Date', type: 'date', required: true },
      { key: 'duration', label: 'Duration', type: 'text' },
      { key: 'training_type', label: 'Type', type: 'text' },
      { key: 'goals', label: 'Goals', type: 'text' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    pdfType: 'training',
  },
  expenses: {
    collection: 'expenses',
    label: 'Expense Tracker',
    icon: DollarSign,
    bg: '#E9EFE8',
    fields: [
      { key: 'date', label: 'Date', type: 'date', required: true },
      { key: 'category', label: 'Category', type: 'text' },
      { key: 'description', label: 'Description', type: 'text' },
      { key: 'amount', label: 'Amount ($)', type: 'number' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    pdfType: 'expenses',
  },
  breeding: {
    collection: 'breeding_records',
    label: 'Breeding Log',
    icon: Heart,
    bg: '#F9F7F3',
    fields: [
      { key: 'date', label: 'Date', type: 'date', required: true },
      { key: 'mare_name', label: 'Mare Name', type: 'text' },
      { key: 'stallion_name', label: 'Stallion Name', type: 'text' },
      { key: 'breeding_type', label: 'Breeding Type', type: 'text' },
      { key: 'heat_cycle_status', label: 'Heat Cycle Status', type: 'text' },
      { key: 'pedigree_info', label: 'Pedigree Info', type: 'textarea' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    pdfType: 'breeding',
  },
  shows: {
    collection: 'show_records',
    label: 'Show Records',
    icon: Trophy,
    bg: '#FFFFFF',
    fields: [
      { key: 'date', label: 'Date', type: 'date', required: true },
      { key: 'show_name', label: 'Show Name', type: 'text' },
      { key: 'category', label: 'Category', type: 'text' },
      { key: 'placement', label: 'Placement', type: 'text' },
      { key: 'money_earned', label: 'Money Earned ($)', type: 'number' },
      { key: 'notes', label: 'Notes', type: 'textarea' },
    ],
    pdfType: 'shows',
  },
};

function RecordTab({ config, horseId, authHeaders, API, hasFullAccess }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [form, setForm] = useState({});
  const [sortOrder, setSortOrder] = useState('newest');
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [expandedPhotos, setExpandedPhotos] = useState(null);
  const [uploading, setUploading] = useState(null);

  const emptyForm = useCallback(() => {
    const f = {};
    config.fields.forEach(field => { f[field.key] = field.type === 'number' ? 0 : ''; });
    return f;
  }, [config.fields]);

  const fetchRecords = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/${config.collection}/${horseId}`, authHeaders());
      setRecords(res.data);
    } catch (err) {
      toast.error(`Failed to load ${config.label.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  }, [API, config.collection, config.label, horseId, authHeaders]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  // Fetch expense summary if this is the expenses tab
  useEffect(() => {
    if (config.collection === 'expenses') {
      axios.get(`${API}/expenses/${horseId}/summary`, authHeaders())
        .then(res => setExpenseSummary(res.data))
        .catch(() => {});
    }
  }, [API, horseId, config.collection, authHeaders, records]); // eslint-disable-line

  const sortedRecords = [...records].sort((a, b) => {
    const dateA = a.date || '';
    const dateB = b.date || '';
    return sortOrder === 'newest' ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
  });

  const handleMarkComplete = async (record) => {
    try {
      await axios.put(`${API}/${config.collection}/${record.id}`, { ...record, completed: true }, authHeaders());
      toast.success('Marked as complete!');
      fetchRecords();
    } catch (err) {
      toast.error('Failed to mark complete');
    }
  };

  const handlePhotoUpload = async (recordId, file) => {
    setUploading(recordId);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/records/${config.collection}/${recordId}/photo`, formData, {
        ...authHeaders(),
        headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo uploaded!');
      fetchRecords();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload');
    } finally {
      setUploading(null);
    }
  };

  const handlePhotoDelete = async (recordId, photoId) => {
    try {
      await axios.delete(`${API}/records/${config.collection}/${recordId}/photo/${photoId}`, authHeaders());
      toast.success('Photo removed');
      fetchRecords();
    } catch (err) {
      toast.error('Failed to remove photo');
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const data = { ...form };
    if (data.amount !== undefined) data.amount = parseFloat(data.amount) || 0;
    if (data.money_earned !== undefined) data.money_earned = parseFloat(data.money_earned) || 0;
    try {
      if (editRecord) {
        await axios.put(`${API}/${config.collection}/${editRecord.id}`, data, authHeaders());
        toast.success('Record updated!');
      } else {
        await axios.post(`${API}/${config.collection}/${horseId}`, data, authHeaders());
        toast.success('Record added!');
      }
      setShowDialog(false);
      setEditRecord(null);
      setForm(emptyForm());
      fetchRecords();
    } catch (err) {
      toast.error('Failed to save record');
    }
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      await axios.delete(`${API}/${config.collection}/${recordId}`, authHeaders());
      toast.success('Record deleted');
      fetchRecords();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const openEdit = (record) => {
    setEditRecord(record);
    const f = {};
    config.fields.forEach(field => { f[field.key] = record[field.key] || (field.type === 'number' ? 0 : ''); });
    setForm(f);
    setShowDialog(true);
  };

  const openAdd = () => {
    setEditRecord(null);
    setForm(emptyForm());
    setShowDialog(true);
  };

  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/pdf/${horseId}?record_type=${config.pdfType}`, {
        ...authHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${config.label.replace(/ /g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (err) {
      toast.error('Failed to generate PDF');
    }
  };

  const tableFields = config.fields.filter(f => f.type !== 'textarea');

  return (
    <div>
      {/* Expense Summary */}
      {config.collection === 'expenses' && expenseSummary && expenseSummary.count > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="bg-[#E9EFE8] rounded-xl p-3 text-center">
            <p className="text-[10px] font-semibold text-[#4B6B58] uppercase">Total</p>
            <p className="font-['Cormorant_Garamond'] text-lg font-bold text-[#1B4332]">${expenseSummary.total.toLocaleString()}</p>
          </div>
          {Object.entries(expenseSummary.yearly).slice(-2).map(([year, amount]) => (
            <div key={year} className="bg-[#F9F7F3] rounded-xl p-3 text-center">
              <p className="text-[10px] font-semibold text-[#4B6B58] uppercase">{year}</p>
              <p className="font-['Cormorant_Garamond'] text-lg font-bold text-[#1B4332]">${amount.toLocaleString()}</p>
            </div>
          ))}
          {Object.entries(expenseSummary.monthly).slice(-1).map(([month, amount]) => (
            <div key={month} className="bg-white border border-[#D1E0D5] rounded-xl p-3 text-center">
              <p className="text-[10px] font-semibold text-[#4B6B58] uppercase">{month}</p>
              <p className="font-['Cormorant_Garamond'] text-lg font-bold text-[#1B4332]">${amount.toLocaleString()}</p>
            </div>
          ))}
        </div>
      )}

      {/* Action Bar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {hasFullAccess && (
          <button
            onClick={openAdd}
            data-testid={`add-${config.collection}-button`}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1B4332] text-white text-sm font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F]"
          >
            <Plus size={16} /> Add Record
          </button>
        )}
        {hasFullAccess && (
          <button
            onClick={downloadPdf}
            data-testid={`download-pdf-${config.collection}`}
            className="flex items-center gap-2 px-5 py-2.5 rounded-full border-2 border-[#1B4332] text-[#1B4332] text-sm font-semibold btn-lift hover:bg-[#1B4332] hover:text-white"
          >
            <FileDown size={16} /> Download PDF
          </button>
        )}
        <button
          onClick={() => setSortOrder(s => s === 'newest' ? 'oldest' : 'newest')}
          data-testid={`sort-${config.collection}`}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-white text-[#4B6B58] text-sm font-medium border border-[#D1E0D5] hover:bg-[#E9EFE8]"
        >
          <ArrowUpDown size={14} /> {sortOrder === 'newest' ? 'Newest First' : 'Oldest First'}
        </button>
        {!hasFullAccess && (
          <div className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FEF3C7] text-[#92400E] text-sm font-medium" data-testid="access-restricted-banner">
            Subscribe to add, edit, or export records
          </div>
        )}
      </div>

      {/* Table */}
      <div className="rounded-[1.5rem] overflow-hidden shadow-[0_4px_12px_rgb(27,67,50,0.04)]" style={{ backgroundColor: config.bg }}>
        {loading ? (
          <div className="p-8 text-center text-[#4B6B58] animate-pulse">Loading records...</div>
        ) : records.length === 0 ? (
          <div className="p-10 text-center">
            <config.icon size={32} className="text-[#4B6B58]/40 mx-auto mb-3" />
            <p className="text-[#4B6B58] text-sm">No records yet. Add your first record above.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table className="stable-table">
              <TableHeader>
                <TableRow className="border-b border-[#D1E0D5]">
                  {tableFields.map(f => (
                    <TableHead key={f.key} className="text-[#4B6B58] font-semibold text-xs uppercase tracking-wider px-4 py-3">{f.label}</TableHead>
                  ))}
                  <TableHead className="text-[#4B6B58] font-semibold text-xs uppercase tracking-wider px-4 py-3 w-[140px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRecords.map(record => (
                  <React.Fragment key={record.id}>
                    <TableRow className={`border-b border-[#D1E0D5]/50 hover:bg-[#E9EFE8]/30 ${record.completed ? 'opacity-50' : ''}`} data-testid={`record-row-${record.id}`}>
                      {tableFields.map(f => {
                        const field = config.fields.find(cf => cf.key === f.key);
                        let display = record[f.key] || '-';
                        if (f.key === 'appointment_time') display = record[f.key] || '-';
                        else if (f.key === 'amount' || f.key === 'money_earned' || f.key === 'cost') {
                          display = <span>${parseFloat(record[f.key] || 0).toFixed(2)}{record.shared_expense && <span className="ml-1 text-[10px] text-[#7C3AED] bg-[#F3E8FF] px-1.5 py-0.5 rounded-full">shared</span>}</span>;
                        } else if (field?.type === 'select' && field?.options) {
                          const opt = field.options.find(o => o.value === record[f.key]);
                          display = opt ? opt.label : (record[f.key] || '-');
                        }
                        return <TableCell key={f.key} className="px-4 py-4 text-sm text-[#122A1F]">{display}</TableCell>;
                      })}
                      <TableCell className="px-4 py-4">
                        {hasFullAccess && (
                          <div className="flex gap-1">
                            {config.hasComplete && !record.completed && (
                              <button onClick={() => handleMarkComplete(record)} data-testid={`complete-${record.id}`} className="p-2 rounded-full hover:bg-green-50 text-green-500" title="Mark complete">
                                <CheckCircle2 size={14} />
                              </button>
                            )}
                            {config.hasComplete && record.completed && (
                              <span className="p-2 text-green-400" title="Completed"><Check size={14} /></span>
                            )}
                            <label className="p-2 rounded-full hover:bg-[#E9EFE8] text-[#4B6B58] cursor-pointer relative" title="Upload receipt/photo">
                              {uploading === record.id ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={e => e.target.files[0] && handlePhotoUpload(record.id, e.target.files[0])} />
                            </label>
                            {(record.photos?.length > 0) && (
                              <button onClick={() => setExpandedPhotos(expandedPhotos === record.id ? null : record.id)} data-testid={`photos-${record.id}`} className="p-2 rounded-full hover:bg-[#E9EFE8] text-[#2D6A4F] relative" title={`${record.photos.length} photo(s)`}>
                                <ImageIcon size={14} />
                                <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#2D6A4F] text-white text-[8px] font-bold rounded-full flex items-center justify-center">{record.photos.length}</span>
                              </button>
                            )}
                            <button onClick={() => openEdit(record)} data-testid={`edit-record-${record.id}`} className="p-2 rounded-full hover:bg-[#E9EFE8] text-[#4B6B58]"><Edit2 size={14} /></button>
                            <button onClick={() => handleDelete(record.id)} data-testid={`delete-record-${record.id}`} className="p-2 rounded-full hover:bg-red-50 text-red-400"><Trash2 size={14} /></button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                    {/* Expanded photos row */}
                    {expandedPhotos === record.id && record.photos?.length > 0 && (
                      <TableRow className="bg-[#F9F7F3]">
                        <TableCell colSpan={tableFields.length + 1} className="px-4 py-3">
                          <div className="flex flex-wrap gap-3">
                            {record.photos.map(photo => (
                              <div key={photo.id} className="relative group">
                                {photo.content_type === 'application/pdf' ? (
                                  <a href={photo.path} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-[#D1E0D5] hover:border-[#2D6A4F] text-sm text-[#1B4332]">
                                    <FileDown size={16} /> {photo.filename || 'Receipt.pdf'}
                                  </a>
                                ) : (
                                  <a href={photo.path} target="_blank" rel="noopener noreferrer">
                                    <img src={photo.path} alt={photo.filename} className="w-20 h-20 object-cover rounded-lg border border-[#D1E0D5]" />
                                  </a>
                                )}
                                {hasFullAccess && (
                                  <button onClick={() => handlePhotoDelete(record.id, photo.id)} className="absolute -top-2 -right-2 w-5 h-5 bg-red-400 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" title="Remove">
                                    <X size={10} />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[#F2F7F4] border-[#D1E0D5] rounded-[1.5rem] max-w-md" data-testid={`${config.collection}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332]">
              {editRecord ? `Edit ${config.label}` : `Add ${config.label}`}
            </DialogTitle>
            <DialogDescription className="text-[#4B6B58]">
              {editRecord ? 'Update this record.' : 'Fill in the details below.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 mt-4">
            {config.fields.map(field => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-[#122A1F] mb-1">
                  {field.label} {field.required && '*'}
                </label>
                {field.type === 'textarea' ? (
                  <textarea
                    value={form[field.key] || ''}
                    onChange={e => setForm({...form, [field.key]: e.target.value})}
                    required={field.required}
                    data-testid={`input-${field.key}`}
                    className="stable-input w-full"
                    rows={3}
                    placeholder={`Enter ${field.label.toLowerCase()}...`}
                  />
                ) : field.type === 'select' ? (
                  <select
                    value={form[field.key] || ''}
                    onChange={e => setForm({...form, [field.key]: e.target.value})}
                    required={field.required}
                    data-testid={`input-${field.key}`}
                    className="stable-input w-full"
                  >
                    {field.options.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : field.type === 'time' ? 'time' : 'text'}
                    step={field.type === 'number' ? '0.01' : undefined}
                    value={form[field.key] || ''}
                    onChange={e => setForm({...form, [field.key]: e.target.value})}
                    required={field.required}
                    data-testid={`input-${field.key}`}
                    className="stable-input w-full"
                    placeholder={field.type === 'date' || field.type === 'time' ? '' : `Enter ${field.label.toLowerCase()}...`}
                  />
                )}
              </div>
            ))}
            <button type="submit" data-testid={`save-${config.collection}`} className="w-full py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift hover:bg-[#2D6A4F]">
              {editRecord ? 'Update Record' : 'Add Record'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function computeAge(dob) {
  if (!dob) return null;
  const birth = new Date(dob + 'T00:00:00');
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (today.getMonth() < birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())) age--;
  return Math.max(0, age);
}

function AIInsightsTab({ horseId, authHeaders, API, hasFullAccess }) {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchInsights = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/horses/${horseId}/insights`, authHeaders());
      setInsights(res.data);
    } catch (err) {
      toast.error('Failed to load AI insights');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={fetchInsights}
          disabled={loading || !hasFullAccess}
          data-testid="generate-insights-button"
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1B4332] text-white text-sm font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F] disabled:opacity-50"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
          {loading ? 'Analyzing...' : 'Generate AI Insights'}
        </button>
        {!hasFullAccess && (
          <span className="text-xs text-[#92400E] bg-[#FEF3C7] px-3 py-1.5 rounded-full">Subscribe for AI insights</span>
        )}
      </div>

      {insights && (
        <div className="space-y-6 animate-fade-in">
          {/* Overall Health */}
          <div className="bg-white rounded-[1.5rem] p-6 shadow-[0_4px_12px_rgb(27,67,50,0.04)]" data-testid="insights-overall">
            <h3 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#1B4332] mb-3">Overall Health Assessment</h3>
            <p className="text-sm text-[#122A1F] leading-relaxed">{insights.overall_health}</p>
          </div>

          {/* Alerts */}
          {insights.alerts?.length > 0 && (
            <div className="bg-[#FEF3C7] rounded-[1.5rem] p-6" data-testid="insights-alerts">
              <h3 className="font-semibold text-[#92400E] text-sm mb-3 uppercase tracking-wider">Alerts</h3>
              <ul className="space-y-2">
                {insights.alerts.map((alert, i) => (
                  <li key={i} className="text-sm text-[#92400E] flex gap-2">
                    <span className="text-[#92400E] font-bold flex-shrink-0">!</span>
                    <span>{typeof alert === 'string' ? alert : alert.message || JSON.stringify(alert)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommendations */}
          {insights.recommendations?.length > 0 && (
            <div className="bg-[#E9EFE8] rounded-[1.5rem] p-6" data-testid="insights-recommendations">
              <h3 className="font-semibold text-[#2D6A4F] text-sm mb-3 uppercase tracking-wider">Recommendations</h3>
              <ul className="space-y-2">
                {insights.recommendations.map((rec, i) => (
                  <li key={i} className="text-sm text-[#122A1F] flex gap-2">
                    <span className="text-[#2D6A4F] font-bold flex-shrink-0">{i + 1}.</span>
                    <span>{typeof rec === 'string' ? rec : rec.message || JSON.stringify(rec)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Upcoming */}
          {insights.upcoming?.length > 0 && (
            <div className="bg-[#F9F7F3] rounded-[1.5rem] p-6" data-testid="insights-upcoming">
              <h3 className="font-semibold text-[#8B6914] text-sm mb-3 uppercase tracking-wider">Upcoming Care Needs</h3>
              <ul className="space-y-2">
                {insights.upcoming.map((item, i) => (
                  <li key={i} className="text-sm text-[#122A1F] flex gap-2">
                    <span className="text-[#8B6914] font-bold flex-shrink-0">-</span>
                    <span>{typeof item === 'string' ? item : item.message || JSON.stringify(item)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {!insights && !loading && (
        <div className="bg-white rounded-[1.5rem] p-10 text-center shadow-[0_4px_12px_rgb(27,67,50,0.04)]">
          <Sparkles size={32} className="text-[#4B6B58]/40 mx-auto mb-3" />
          <p className="text-sm text-[#4B6B58]">Click "Generate AI Insights" to analyze your horse's health records.</p>
          <p className="text-xs text-[#4B6B58] mt-1">AI will review vaccinations, vet visits, medications, and more.</p>
        </div>
      )}
    </div>
  );
}

function PhotoGalleryTab({ horseId, authHeaders, API, hasFullAccess, token }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchPhotos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/horses/${horseId}/gallery`, authHeaders());
      setPhotos(res.data);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  }, [API, horseId, authHeaders]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image'); return; }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${API}/horses/${horseId}/gallery`, formData, {
        headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo uploaded!');
      fetchPhotos();
    } catch (err) {
      toast.error('Failed to upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    try {
      await axios.delete(`${API}/horses/${horseId}/gallery/${photoId}`, authHeaders());
      toast.success('Photo deleted');
      fetchPhotos();
    } catch (err) { toast.error('Failed to delete'); }
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        {hasFullAccess && (
          <label className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1B4332] text-white text-sm font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F] cursor-pointer" data-testid="gallery-upload-button">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            {uploading ? 'Uploading...' : 'Upload Photo'}
            <input type="file" accept="image/*" onChange={handleUpload} className="hidden" disabled={uploading} data-testid="gallery-file-input" />
          </label>
        )}
        {!hasFullAccess && (
          <span className="text-xs text-[#92400E] bg-[#FEF3C7] px-3 py-1.5 rounded-full">Subscribe to upload photos</span>
        )}
      </div>

      {loading ? (
        <div className="text-center text-[#4B6B58] animate-pulse py-8">Loading gallery...</div>
      ) : photos.length === 0 ? (
        <div className="bg-white rounded-[1.5rem] p-10 text-center shadow-[0_4px_12px_rgb(27,67,50,0.04)]">
          <ImageIcon size={32} className="text-[#4B6B58]/40 mx-auto mb-3" />
          <p className="text-sm text-[#4B6B58]">No photos yet. Upload your first photo above.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="gallery-grid">
          {photos.map(photo => (
            <GalleryPhoto key={photo.id} photo={photo} onDelete={handleDelete} API={API} token={token} hasFullAccess={hasFullAccess} />
          ))}
        </div>
      )}
    </div>
  );
}

function GalleryPhoto({ photo, onDelete, API, token, hasFullAccess }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let blobUrl = null;
    if (photo.photo_path) {
      axios.get(`${API}/files/${photo.photo_path}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      }).then(res => {
        blobUrl = URL.createObjectURL(res.data);
        setUrl(blobUrl);
      }).catch(() => {});
    }
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [photo.photo_path, API, token]);

  return (
    <div className="relative group rounded-[1rem] overflow-hidden bg-[#E9EFE8] aspect-square shadow-[0_4px_12px_rgb(27,67,50,0.04)]" data-testid={`gallery-photo-${photo.id}`}>
      {url ? (
        <img src={url} alt={photo.caption || 'Horse photo'} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center"><Loader2 size={20} className="text-[#4B6B58] animate-spin" /></div>
      )}
      {hasFullAccess && (
        <button
          onClick={() => onDelete(photo.id)}
          data-testid={`delete-gallery-${photo.id}`}
          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-red-500/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Trash2 size={12} />
        </button>
      )}
      {photo.caption && (
        <div className="absolute bottom-0 left-0 right-0 bg-black/50 px-3 py-1.5">
          <p className="text-xs text-white truncate">{photo.caption}</p>
        </div>
      )}
    </div>
  );
}

const TIMELINE_ICONS = {
  'Vaccination': { icon: Syringe, color: '#2D6A4F', bg: '#E9EFE8' },
  'Vet Visit': { icon: Stethoscope, color: '#92400E', bg: '#FEF3C7' },
  'Medication': { icon: Pill, color: '#7C3AED', bg: '#F3E8FF' },
  'Farrier': { icon: Scissors, color: '#8B6914', bg: '#F9F7F3' },
  'Training': { icon: BookOpen, color: '#1D4ED8', bg: '#DBEAFE' },
  'Expense': { icon: DollarSign, color: '#DC2626', bg: '#FEE2E2' },
  'Breeding': { icon: Heart, color: '#DB2777', bg: '#FCE7F3' },
  'Show': { icon: Trophy, color: '#D97706', bg: '#FEF3C7' },
};

function HealthTimelineTab({ horseId, authHeaders, API }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/horses/${horseId}/timeline`, authHeaders())
      .then(res => setEvents(res.data))
      .catch(() => toast.error('Failed to load timeline'))
      .finally(() => setLoading(false));
  }, [horseId, API, authHeaders]);

  // Group events by year+month
  const grouped = {};
  events.forEach(e => {
    if (!e.date) return;
    const key = e.date.substring(0, 7); // YYYY-MM
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(e);
  });

  const monthKeys = Object.keys(grouped).sort().reverse();

  return (
    <div>
      {loading ? (
        <div className="text-center text-[#4B6B58] animate-pulse py-8">Loading timeline...</div>
      ) : events.length === 0 ? (
        <div className="bg-white rounded-[1.5rem] p-10 text-center shadow-[0_4px_12px_rgb(27,67,50,0.04)]">
          <Clock size={32} className="text-[#4B6B58]/40 mx-auto mb-3" />
          <p className="text-sm text-[#4B6B58]">No events yet. Add records to see them on the timeline.</p>
        </div>
      ) : (
        <div className="relative" data-testid="health-timeline">
          {/* Vertical line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-[#D1E0D5]" />

          {monthKeys.map(monthKey => {
            const monthDate = new Date(monthKey + '-01T12:00:00');
            const monthLabel = monthDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

            return (
              <div key={monthKey} className="mb-8">
                {/* Month header */}
                <div className="flex items-center gap-3 mb-4 relative z-10">
                  <div className="w-12 h-12 rounded-full bg-[#1B4332] text-white flex items-center justify-center flex-shrink-0 shadow-[0_4px_12px_rgb(27,67,50,0.15)]">
                    <span className="text-xs font-bold">{monthDate.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()}</span>
                  </div>
                  <h3 className="font-['Cormorant_Garamond'] text-lg font-bold text-[#1B4332]">{monthLabel}</h3>
                </div>

                {/* Events in this month */}
                <div className="space-y-3 ml-6 pl-8 border-l-0">
                  {grouped[monthKey].map((event, idx) => {
                    const config = TIMELINE_ICONS[event.type] || TIMELINE_ICONS['Vaccination'];
                    const Icon = config.icon;
                    const eventDate = new Date(event.date + 'T12:00:00');
                    const dayStr = eventDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

                    return (
                      <div key={event.id || idx} className="flex items-start gap-3 relative" data-testid={`timeline-event-${event.id || idx}`}>
                        {/* Dot on the line */}
                        <div className="absolute -left-[2.55rem] top-2 w-3 h-3 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: config.color }} />

                        <div className="flex-1 bg-white rounded-[1rem] p-4 shadow-[0_2px_8px_rgb(27,67,50,0.04)] hover:shadow-[0_4px_12px_rgb(27,67,50,0.08)] transition-shadow">
                          <div className="flex items-center gap-2 mb-1.5">
                            <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ backgroundColor: config.bg }}>
                              <Icon size={13} style={{ color: config.color }} />
                            </div>
                            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: config.color }}>{event.type}</span>
                            <span className="text-xs text-[#4B6B58] ml-auto">{dayStr}</span>
                          </div>
                          {event.detail && <p className="text-sm font-medium text-[#122A1F]">{event.detail}</p>}
                          {event.notes && <p className="text-xs text-[#4B6B58] mt-1 line-clamp-2">{event.notes}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function HorseDetailPage() {
  const { horseId } = useParams();
  const { authHeaders, API, token, hasFullAccess } = useAuth();
  const [horse, setHorse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploading, setUploading] = useState(false);

  const fetchHorse = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/horses`, authHeaders());
      const found = res.data.find(h => h.id === horseId);
      if (found) setHorse(found);
    } catch (err) {
      toast.error('Failed to load horse');
    } finally {
      setLoading(false);
    }
  }, [horseId, API, authHeaders]);

  useEffect(() => { fetchHorse(); }, [fetchHorse]);

  // Share state
  const [shareLink, setShareLink] = useState(null);
  const [shares, setShares] = useState([]);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const isOwner = horse?.is_owner !== false; // true if not set or true

  const generateShareLink = async () => {
    setShareLoading(true);
    try {
      const res = await axios.post(`${API}/horses/${horseId}/share`, {}, authHeaders());
      const link = `${window.location.origin}/share/${res.data.share_code}`;
      setShareLink(link);
      fetchShares();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate share link');
    } finally {
      setShareLoading(false);
    }
  };

  const fetchShares = async () => {
    try {
      const res = await axios.get(`${API}/horses/${horseId}/shares`, authHeaders());
      setShares(res.data);
    } catch { /* not owner */ }
  };

  const revokeShare = async (shareId) => {
    if (!window.confirm('Revoke this share? The user will lose access.')) return;
    try {
      await axios.delete(`${API}/horses/share/${shareId}`, authHeaders());
      toast.success('Share revoked');
      fetchShares();
    } catch { toast.error('Failed to revoke'); }
  };

  const copyLink = () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success('Share link copied!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  useEffect(() => {
    if (horse && isOwner) fetchShares();
  }, [horse]); // eslint-disable-line

  // Separate effect for photo loading when horse data changes
  useEffect(() => {
    let blobUrl = null;
    if (horse?.photo_path) {
      axios.get(`${API}/files/${horse.photo_path}`, {
        ...authHeaders(),
        responseType: 'blob'
      }).then(res => {
        blobUrl = URL.createObjectURL(res.data);
        setPhotoUrl(blobUrl);
      }).catch(() => {});
    }
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [horse?.photo_path, API, authHeaders]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/horses/${horseId}/photo`, formData, {
        headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      if (res.data.photo_path) {
        setHorse(prev => ({ ...prev, photo_path: res.data.photo_path }));
      }
      toast.success('Photo uploaded!');
    } catch (err) {
      toast.error('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const downloadAllPdf = async () => {
    try {
      const res = await axios.get(`${API}/pdf/${horseId}?record_type=all`, {
        ...authHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${horse?.name || 'Horse'}_All_Records_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Complete PDF downloaded!');
    } catch (err) {
      toast.error('Failed to generate PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F7F4]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <div className="animate-pulse text-[#4B6B58] text-lg">Loading horse details...</div>
        </div>
      </div>
    );
  }

  if (!horse) {
    return (
      <div className="min-h-screen bg-[#F2F7F4]">
        <Navbar />
        <div className="max-w-7xl mx-auto px-4 py-12 text-center">
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332]">Horse not found</h2>
          <Link to="/dashboard" className="text-[#2D6A4F] underline mt-4 inline-block">Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back & Horse Header */}
        <div className="animate-fade-in mb-8">
          <Link to="/dashboard" className="flex items-center gap-2 text-[#4B6B58] text-sm mb-4 hover:text-[#1B4332] btn-lift w-fit" data-testid="back-to-dashboard">
            <ArrowLeft size={16} /> Back to Dashboard
          </Link>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-5">
              {/* Horse Photo */}
              <div className="relative flex-shrink-0" data-testid="horse-photo-section">
                <div className="w-24 h-24 rounded-[1.5rem] overflow-hidden bg-[#E9EFE8] shadow-[0_4px_12px_rgb(27,67,50,0.08)]">
                  {photoUrl ? (
                    <img src={photoUrl} alt={horse.name} className="w-full h-full object-cover" data-testid="horse-photo" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Camera size={28} className="text-[#4B6B58]/40" />
                    </div>
                  )}
                </div>
                <label className={`absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#1B4332] text-white flex items-center justify-center ${hasFullAccess ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'} btn-lift hover:bg-[#2D6A4F] shadow-[0_2px_8px_rgb(27,67,50,0.2)]`} data-testid="upload-photo-button">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                  <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" disabled={uploading || !hasFullAccess} data-testid="photo-file-input" />
                </label>
              </div>
              <div>
                <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332]" data-testid="horse-detail-name">
                  {horse.name}
                </h1>
                <div className="flex flex-wrap gap-2 mt-2">
                  {horse.breed && <span className="px-3 py-1 rounded-full bg-[#E9EFE8] text-xs text-[#4B6B58] font-medium">{horse.breed}</span>}
                  {horse.color && <span className="px-3 py-1 rounded-full bg-[#F9F7F3] text-xs text-[#4B6B58] font-medium">{horse.color}</span>}
                  {horse.date_of_birth && <span className="px-3 py-1 rounded-full bg-[#E9EFE8] text-xs text-[#4B6B58] font-medium">{computeAge(horse.date_of_birth)} years old</span>}
                  {horse.date_of_birth && <span className="px-3 py-1 rounded-full bg-[#F9F7F3] text-xs text-[#4B6B58] font-medium">DOB: {new Date(horse.date_of_birth + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>}
                  {horse.gender && <span className="px-3 py-1 rounded-full bg-[#E9EFE8] text-xs text-[#4B6B58] font-medium">{horse.gender}</span>}
                  {horse.weight && <span className="px-3 py-1 rounded-full bg-[#F9F7F3] text-xs text-[#4B6B58] font-medium">{horse.weight} lbs</span>}
                  {horse.registration_association && (
                    <span className="px-3 py-1 rounded-full bg-[#2D6A4F]/10 text-xs text-[#2D6A4F] font-semibold">
                      {horse.registration_association}{horse.registration_number ? ` #${horse.registration_number}` : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
            {hasFullAccess && (
              <div className="flex items-center gap-3">
                {isOwner && (
                  <button
                    onClick={() => { setShowShareDialog(true); setShareLink(null); }}
                    data-testid="share-horse-button"
                    className="flex items-center gap-2 px-5 py-3 rounded-full border-2 border-[#1B4332] text-[#1B4332] font-semibold btn-lift hover:bg-[#1B4332] hover:text-white"
                  >
                    <Share2 size={18} /> Share
                  </button>
                )}
                <button
                  onClick={downloadAllPdf}
                  data-testid="download-all-pdf"
                  className="flex items-center gap-2 px-6 py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift shadow-[0_4px_12px_rgb(27,67,50,0.15)] hover:bg-[#2D6A4F]"
                >
                  <FileDown size={18} /> Download PDF
                </button>
              </div>
            )}
            {horse.is_shared && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#7C3AED]/10 text-[#7C3AED] text-sm font-semibold" data-testid="shared-horse-indicator">
                <Share2 size={14} /> Shared by {horse.shared_by || 'Owner'}
              </div>
            )}
          </div>
        </div>

        {/* Record Tabs */}
        <Tabs defaultValue="vaccinations" className="animate-fade-in-delay-1">
          <div className="overflow-x-auto pb-2 -mx-4 px-4">
            <TabsList className="inline-flex h-auto p-1.5 bg-white rounded-[1.5rem] shadow-[0_4px_12px_rgb(27,67,50,0.04)] gap-1" data-testid="record-tabs">
              {Object.entries(RECORD_CONFIGS).map(([key, config]) => (
                <TabsTrigger
                  key={key}
                  value={key}
                  data-testid={`tab-${key}`}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-[1rem] text-sm font-medium data-[state=active]:bg-[#1B4332] data-[state=active]:text-white data-[state=active]:shadow-[0_4px_12px_rgb(27,67,50,0.15)] text-[#4B6B58] whitespace-nowrap"
                >
                  <config.icon size={15} />
                  <span className="hidden sm:inline">{config.label}</span>
                  <span className="sm:hidden">{config.label.split(' ')[0]}</span>
                </TabsTrigger>
              ))}
              <TabsTrigger
                value="ai_insights"
                data-testid="tab-ai-insights"
                className="flex items-center gap-2 px-4 py-2.5 rounded-[1rem] text-sm font-medium data-[state=active]:bg-[#1B4332] data-[state=active]:text-white data-[state=active]:shadow-[0_4px_12px_rgb(27,67,50,0.15)] text-[#4B6B58] whitespace-nowrap"
              >
                <Sparkles size={15} />
                <span className="hidden sm:inline">AI Insights</span>
                <span className="sm:hidden">AI</span>
              </TabsTrigger>
              <TabsTrigger
                value="gallery"
                data-testid="tab-gallery"
                className="flex items-center gap-2 px-4 py-2.5 rounded-[1rem] text-sm font-medium data-[state=active]:bg-[#1B4332] data-[state=active]:text-white data-[state=active]:shadow-[0_4px_12px_rgb(27,67,50,0.15)] text-[#4B6B58] whitespace-nowrap"
              >
                <ImageIcon size={15} />
                <span className="hidden sm:inline">Photo Gallery</span>
                <span className="sm:hidden">Gallery</span>
              </TabsTrigger>
              <TabsTrigger
                value="timeline"
                data-testid="tab-timeline"
                className="flex items-center gap-2 px-4 py-2.5 rounded-[1rem] text-sm font-medium data-[state=active]:bg-[#1B4332] data-[state=active]:text-white data-[state=active]:shadow-[0_4px_12px_rgb(27,67,50,0.15)] text-[#4B6B58] whitespace-nowrap"
              >
                <Clock size={15} />
                <span className="hidden sm:inline">Health Timeline</span>
                <span className="sm:hidden">Timeline</span>
              </TabsTrigger>
            </TabsList>
          </div>

          {Object.entries(RECORD_CONFIGS).map(([key, config]) => (
            <TabsContent key={key} value={key} className="mt-6">
              <RecordTab config={config} horseId={horseId} authHeaders={authHeaders} API={API} hasFullAccess={hasFullAccess} />
            </TabsContent>
          ))}
          <TabsContent value="ai_insights" className="mt-6">
            <AIInsightsTab horseId={horseId} authHeaders={authHeaders} API={API} hasFullAccess={hasFullAccess} />
          </TabsContent>
          <TabsContent value="gallery" className="mt-6">
            <PhotoGalleryTab horseId={horseId} authHeaders={authHeaders} API={API} hasFullAccess={hasFullAccess} token={token} />
          </TabsContent>
          <TabsContent value="timeline" className="mt-6">
            <HealthTimelineTab horseId={horseId} authHeaders={authHeaders} API={API} />
          </TabsContent>
        </Tabs>
      </main>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="bg-[#F2F7F4] border-[#D1E0D5] rounded-[1.5rem] max-w-md" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Cormorant_Garamond'] text-2xl font-bold text-[#1B4332]">
              Share {horse?.name}
            </DialogTitle>
            <DialogDescription className="text-[#4B6B58]">
              Generate a link to share this horse's records. The recipient can view all records and add new ones.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {!shareLink ? (
              <button
                onClick={generateShareLink}
                disabled={shareLoading}
                data-testid="generate-share-link-button"
                className="w-full flex items-center justify-center gap-2 py-3 rounded-full bg-[#1B4332] text-white font-semibold btn-lift hover:bg-[#2D6A4F] disabled:opacity-50"
              >
                {shareLoading ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
                {shareLoading ? 'Generating...' : 'Generate Share Link'}
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center gap-2 bg-white p-3 rounded-xl border border-[#D1E0D5]">
                  <input value={shareLink} readOnly className="flex-1 text-sm text-[#122A1F] bg-transparent outline-none" data-testid="share-link-input" />
                  <button
                    onClick={copyLink}
                    data-testid="copy-share-link-button"
                    className="flex items-center gap-1 px-4 py-2 rounded-full bg-[#1B4332] text-white text-xs font-semibold hover:bg-[#2D6A4F]"
                  >
                    {copied ? <Check size={12} /> : <Copy size={12} />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <p className="text-xs text-[#4B6B58]">Send this link to a trainer, buyer, or anyone you want to share records with. They'll need a Stable Solutions account to accept.</p>
                <button
                  onClick={generateShareLink}
                  className="text-xs text-[#2D6A4F] hover:underline"
                >
                  Generate another link
                </button>
              </div>
            )}

            {/* Active shares */}
            {shares.length > 0 && (
              <div className="border-t border-[#D1E0D5] pt-4">
                <h4 className="text-sm font-semibold text-[#1B4332] mb-3">People with access</h4>
                <div className="space-y-2">
                  {shares.map(share => (
                    <div key={share.id} className="flex items-center justify-between bg-white p-3 rounded-xl" data-testid={`share-entry-${share.id}`}>
                      <div>
                        {share.status === 'active' ? (
                          <p className="text-sm font-medium text-[#122A1F]">{share.shared_with_name || 'Unknown'}</p>
                        ) : (
                          <p className="text-sm text-[#4B6B58]">Pending invitation</p>
                        )}
                        <p className="text-[10px] text-[#4B6B58]">
                          {share.status === 'active' ? 'Can view & add records' : `Code: ${share.share_code}`}
                        </p>
                      </div>
                      <button
                        onClick={() => revokeShare(share.id)}
                        data-testid={`revoke-share-${share.id}`}
                        className="w-7 h-7 rounded-full bg-[#FEE2E2] flex items-center justify-center text-red-400 hover:text-red-600"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
